import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, Download, ArrowRight } from "lucide-react";

const SLUGS = {
  "01": "shared-vision", "02": "forensic-audit", "04": "constitutional-reforms", "05": "open-government",
  "06": "merit-personnel", "07": "families-first", "08": "casino", "09": "reservation-economy",
  "10": "peoples-fund-protection", "11": "life-and-limb", "12": "culture-youth", "13": "public-safety",
  "14": "elders-veterans", "15": "medical-cannabis", "16": "lakebed-allottees", "17": "no-data-centers",
  "18": "water-rights",
};

const RESOLUTIONS = [
  {
    section: "01",
    title: "Shared Vision & Public Participation",
    text: "Declares that Tribal government belongs to — and answers to — the people: regular community meetings in every segment, public notice, and real input before major decisions.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/yqrw6m7s_01%20-%20Resolution%20-%20Shared%20Vision%20and%20Public%20Participation.docx",
  },
  {
    section: "02",
    title: "Independent Forensic Audit & Accountability Commission",
    text: "Establishes an Independent Forensic Audit and Accountability Commission to review Tribal finances and oversee a forensic audit of major contracts and expenditures — with broad authority to access records and public reporting to restore trust.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/nrdes3bd_02%20-%20Resolution%20-%20Independent%20Audit%20and%20Accountability%20Commission.docx",
  },
  {
    section: "04",
    title: "Returning Power to the People — Constitutional & Election Reforms",
    text: "Establishes a working group to develop constitutional amendments on citizen participation, voting rights, and government structure — and funds their implementation.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/avmemolm_04%20-%20Resolution%20-%20Returning%20Power%20to%20the%20People%20through%20Constitutional%20and%20Election%20Reforms.docx",
  },
  {
    section: "05",
    title: "Open Government — Standing Committees & Citizen Members",
    text: "Creates a dedicated Budget and Finance Committee and seats two citizen members-at-large on every standing committee, before decisions reach the full Council.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/k8s5dzv4_05%20-%20Resolution%20-%20Open%20Government%20through%20Standing%20Committees%20and%20Citizen%20Members.docx",
  },
  {
    section: "06",
    title: "Professional Government & Merit-Based Personnel System",
    text: "Hiring, promotion, and discipline based on qualifications and performance — not favoritism — with trained managers and HR professionals running day-to-day personnel under written policy.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/rjvrr3g3_06%20-%20Resolution%20-%20Professional%20Government%20and%20Meritbased%20Personnel%20System.docx",
  },
  {
    section: "07",
    title: "Putting Families First — Housing & Community Investment",
    text: "Prioritizes safe housing, home ownership, elder and veteran housing, and neighborhood infrastructure — with a funded plan to get it built.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/p2plhg78_07%20-%20Resolution%20-%20Putting%20Families%20First%20through%20Housing%20and%20Community%20Investment.docx",
  },
  {
    section: "08",
    title: "Restoring Four Bears Casino as an Economic Engine",
    text: "An independent review of the casino's operations, finances, and management — and the changes needed to make it a profitable economic driver for the Nation.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/xp2rr3tt_08%20-%20Resolution%20-%20Restoring%20Four%20Bears%20Casino%20as%20An%20Economic%20Engine.docx",
  },
  {
    section: "09",
    title: "Investing in the Reservation Economy & Natural Resources",
    text: "Studies acquiring and operating mature oil wells on Tribal lands and reviews every major Tribal investment — putting the Reservation's own economy and people first.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/w5ve7ewa_09%20-%20Resolution%20-%20Investing%20in%20the%20Reservation%20Economy%20and%20Tribal%20Natural%20Resources.docx",
  },
  {
    section: "10",
    title: "Protection of the People's Fund & Future Generations",
    text: "Locks in the People's Fund for current and future generations — no significant change to the Fund without a vote of the people, with reforms for transparency.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/hv1y3zmn_10%20-%20Resolution%20-%20Protection%20of%20the%20Peoples%20Fund%20and%20Future%20Generations.docx",
  },
  {
    section: "11",
    title: "No Tribal Member Left Behind — Life & Limb Assistance",
    text: "A written, permanent emergency assistance policy for catastrophic illness, injury, fire, and hardship — based on demonstrated need and objective standards, never favoritism.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/tahul0re_11%20-%20Resolution%20-%20No%20Tribal%20Member%20Left%20Behind%20Life%20and%20Limb%20Assistance%20Policy.docx",
  },
  {
    section: "12",
    title: "Cultural Preservation, Language Revitalization & Youth Leadership",
    text: "Makes Mandan, Hidatsa, and Arikara language and cultural preservation permanent government priorities — and creates a Tribal Youth Council with a real voice.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/y7hdvfzf_12%20-%20Resolution%20-%20Cultural%20Preservation%20Language%20Revitalization%20Education%20and%20Youth%20Leadership.docx",
  },
  {
    section: "13",
    title: "Public Safety & Protection of Communities",
    text: "A comprehensive public safety plan strengthening cooperation among Tribal, Federal, State, and local law enforcement and emergency response — with regular public reporting.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/2ba9lk9a_13%20-%20Resolution%20-%20Public%20Safety%20and%20Protection%20of%20Communities.docx",
  },
  {
    section: "14",
    title: "Elders, Veterans & Family Services",
    text: "Strengthens services for elders, veterans, caregivers, and vulnerable members — with a comprehensive, funded Services Plan and public reporting on results.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/ib96iw2y_14%20-%20Resolution%20-%20Elders%20Veterans%20and%20Family%20Services.docx",
  },
  {
    section: "15",
    title: "Responsible Medical Cannabis Program",
    text: "Brings healthcare professionals, law enforcement, and community members together to build a patient-first program with physician oversight and strong safeguards.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/iujqtow9_15%20-%20Resolution%20-%20Responsible%20Medical%20Cannabis%20Program.docx",
  },
  {
    section: "16",
    title: "Lakebed Allottees Advisory Committee",
    text: "Establishes a committee of original allottees, heirs, and legal experts to review lakebed mineral rights and recommend a fair path forward — reported publicly.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/p13u4d04_16%20-%20Resolution%20-%20Lakebed%20Allottees%20Advisory%20Committee%20and%20Original%20Allottee%20Rights.docx",
  },
  {
    section: "17",
    title: "No Industrial-Scale Data Centers",
    text: "Protects our homeland: prohibits industrial-scale data centers on the Fort Berthold Reservation unless the people approve by referendum after full disclosure of impacts.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/3gh0qhn4_17%20-%20Resolution%20-%20Protection%20of%20Land%20No%20Industrialscale%20Data%20Centers%20on%20the%20Fort%20Berthold%20Reservat.docx",
  },
  {
    section: "18",
    title: "Water Rights & West Segment Water Priority",
    text: "Quantifies every water right of the MHA Nation and makes a modern West Segment water system the first priority of a Reservation-wide water strategy.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/vyg2t974_18%20-%20Resolution%20-%20Water%20Rights%20Water%20Infrastructure%20and%20West%20Segment%20Water%20Priority.docx",
  },
];

export default function Resolutions() {
  return (
    <section id="day-one" className="bg-[#F8F9FA] py-24 md:py-36 px-5 md:px-10" data-testid="day-one-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-xs uppercase tracking-[0.3em] font-semibold text-electric mb-6"
          >
            The First 60 Days
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-navy"
          >
            Resolution
            <span className="block italic font-medium text-electric">Solutions</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-navy/70 text-base md:text-lg leading-relaxed mt-8 max-w-md"
          >
            He doesn&rsquo;t just promise. He delivers. Dave has drafted a series of {RESOLUTIONS.length} formal Tribal Council
            resolutions — written, numbered, and ready to introduce within his first 60 days in office.
            Each one takes effect immediately upon adoption.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-10 inline-flex items-center gap-5 border border-navy/15 bg-white rounded-sm px-7 py-5"
          >
            <span className="font-serif text-6xl md:text-7xl font-black text-navy leading-none">60</span>
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-navy/60 leading-relaxed">
              Days to introduce<br />every resolution
            </span>
          </motion.div>
        </div>

        <div className="md:col-span-7 md:pt-8">
          <div className="border-t border-navy/15">
            {RESOLUTIONS.map((r, i) => (
              <motion.article
                key={r.section}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                className="group border-b border-navy/15 py-7 md:py-8 hover:bg-navy/[0.03] transition-colors duration-300 px-2 md:px-4"
                data-testid={`resolution-row-${r.section}`}
              >
                <div className="flex items-start gap-4">
                  <FileText className="w-5 h-5 text-gold mt-1.5 shrink-0" strokeWidth={1.5} />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-electric mb-1.5">
                      Resolution · Shared Vision §{r.section} · Drafted &amp; Ready
                    </p>
                    <h3 className="font-sans text-lg md:text-xl font-bold tracking-tight text-navy group-hover:text-electric transition-colors duration-300">
                      {r.title}
                    </h3>
                    <p className="text-sm md:text-base text-navy/60 mt-2 leading-relaxed max-w-xl">{r.text}</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
                      <Link
                        to={`/resolution/${SLUGS[r.section]}`}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-navy hover:text-electric transition-colors duration-300"
                        data-testid={`resolution-read-${r.section}`}
                      >
                        Read the full text <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-navy/50 hover:text-electric transition-colors duration-300"
                        data-testid={`resolution-download-${r.section}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Original document
                      </a>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xs text-steel italic mt-6 px-2 md:px-4"
          >
            Resolutions from the Shared Vision series — drafted, numbered, and ready for the Council.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
